/**
 * Which catalogue pages a role sees in the sidebar nav. One rule, read by the
 * Sidebar (src/client/components/layout/Sidebar.tsx, nav and paper menu), by
 * tests/apiwall-admin-only-pages-2894.test.ts and by the per-role catalogue
 * matrix (tests/page-catalogue-role-matrix-3711.test.ts), so the three cannot
 * disagree about what a non-admin is shown. It was Sidebar.tsx's private
 * filterNavItems() until FEATREG2 (#3711) made the catalogue generated and
 * needed the rule callable from a test.
 *
 * This decides what the nav OFFERS. Access is enforced by the route guard and
 * the server (isPathAllowed / isPathAllowedWithOverrides, requireAdminMiddleware
 * on the admin APIs); a page hidden here is not thereby secured.
 */

import { isPathAllowed, isUniversalPage } from "./rbac";
import type { SidebarPage, SidebarSection } from "./sidebar-pages";

/** A whole section renders only when it is open to the role (adminOnly sections are admin-only). */
export function isSidebarSectionVisible(section: Pick<SidebarSection, "adminOnly">, isAdmin: boolean): boolean {
  return !(section.adminOnly && !isAdmin);
}

/**
 * One non-divider page's nav visibility for a role. `allowedPages` null means
 * the role is not known yet: fail closed, show nothing. `revokedPages` is
 * /api/rbac/me's `revoked_pages` (#593): a revocable universal page listed
 * there is not shown.
 */
export function isSidebarPageVisible(
  page: Pick<SidebarPage, "path" | "navHidden" | "adminOnly" | "divider">,
  allowedPages: readonly string[] | null,
  isAdmin: boolean,
  revokedPages: readonly string[] = [],
): boolean {
  if (allowedPages === null) return false;
  if (page.divider) return false;
  if (page.navHidden) return false;
  // /admin and its children (User Management, Material Mapping) are admin-only
  // regardless of allowed_pages wildcards — their APIs 403 everyone else.
  if (page.path.startsWith("/admin") && !isAdmin) return false;
  // #2545: a catalog page flagged adminOnly hides from every non-admin nav,
  // even one holding allowed_pages ["*"]. Its API is hard-gated too — this
  // flag only stops management being shown a door that 403s.
  if (page.adminOnly && !isAdmin) return false;
  // Universal pages (own Settings, changelog, guide) show for every role.
  // #593: unless a per-user revoke took a revocable one away.
  if (isUniversalPage(page.path, revokedPages)) return true;
  return isPathAllowed(page.path, [...allowedPages]);
}

/**
 * The section's nav rows for a role, in catalogue order: visible pages plus the
 * dividers that still have a visible page beneath them (an orphan sub-header is
 * dropped). Empty when the section itself is not open to the role.
 */
export function visibleSidebarPages(
  section: SidebarSection,
  allowedPages: readonly string[] | null,
  isAdmin: boolean,
  revokedPages: readonly string[] = [],
): SidebarPage[] {
  if (!isSidebarSectionVisible(section, isAdmin)) return [];
  const kept = section.pages.filter(
    (page) => page.divider || isSidebarPageVisible(page, allowedPages, isAdmin, revokedPages),
  );
  return kept.filter((page, i) => {
    if (!page.divider) return true;
    const next = kept[i + 1];
    return next != null && !next.divider;
  });
}
